import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileText, Shield, Target, RefreshCw, BarChart3, List,
  FileDown, CheckCircle2, Clock, AlertTriangle, ChevronRight,
  Loader2, Eye, Download, Calendar, Globe, Bug, Brain,
  ArrowLeft, Zap, ChevronDown, Activity, Users, Database,
  PieChart, TrendingUp, AlertCircle, Server, Trash2,
  FileCode, FileSpreadsheet, ExternalLink, File, Copy,
  Award, CheckSquare, XSquare, MinusSquare, X, Mail, Lock
} from 'lucide-react';
import { generatedReports } from '../../services/api/client';
import { useI18n } from '../../i18n';
import useAuthStore from '../../store/authStore';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';

// Report types configuration (same as list)
const REPORT_TYPES = {
  executive: { name: 'Rapport Exécutif', icon: BarChart3, color: 'bg-blue-500' },
  technical: { name: 'Rapport Technique', icon: Shield, color: 'bg-green-500' },
  vulnerability_list: { name: 'Liste Vulnérabilités', icon: List, color: 'bg-yellow-500' },
  red_team: { name: 'Rapport Équipe Rouge', icon: Target, color: 'bg-red-500' },
  retest: { name: 'Rapport Re-test', icon: RefreshCw, color: 'bg-purple-500' },
  multi_campaign: { name: 'Multi-Campagnes', icon: TrendingUp, color: 'bg-cyan-500' },
  active_directory: { name: 'Active Directory', icon: Users, color: 'bg-indigo-500' },
  data_leak: { name: 'Fuite Données', icon: Database, color: 'bg-pink-500' },
  attack_surface: { name: 'Surface d\'Attaque', icon: Globe, color: 'bg-orange-500' }
};

// Export formats
const EXPORT_FORMATS = [
  { id: 'pdf', name: 'PDF', icon: FileDown, color: 'text-red-500' },
  { id: 'docx', name: 'Word', icon: FileText, color: 'text-blue-500' },
  { id: 'html', name: 'HTML', icon: FileCode, color: 'text-orange-500' },
  { id: 'json', name: 'JSON', icon: File, color: 'text-yellow-500' },
  { id: 'csv', name: 'CSV', icon: FileSpreadsheet, color: 'text-green-500' },
  { id: 'jira', name: 'Jira', icon: ExternalLink, color: 'text-blue-400' },
  { id: 'servicenow', name: 'ServiceNow', icon: ExternalLink, color: 'text-emerald-500' },
  { id: 'glpi', name: 'GLPI', icon: ExternalLink, color: 'text-purple-500' }
];

// Risk level colors
const RISK_COLORS = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  low: 'bg-green-500/20 text-green-400 border-green-500/30'
};

// Section renderer component
const ReportSection = ({ title, content, icon: Icon, className = '' }) => {
  const [expanded, setExpanded] = useState(true);

  if (!content) return null;

  return (
    <div className={`bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden ${className}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-emerald-500" />}
          <h3 className="font-semibold text-white">{title}</h3>
        </div>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="px-5 pb-5 border-t border-slate-700/50">
          <div className="pt-4">
            {typeof content === 'string' ? (
              <MarkdownRenderer content={content} variant="default" />
            ) : (
              <pre className="bg-slate-900 rounded-lg p-4 text-sm overflow-x-auto text-slate-300">
                {JSON.stringify(content, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Key findings component
const KeyFindings = ({ findings }) => {
  if (!findings || findings.length === 0) return null;

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
      <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
        <AlertCircle className="w-5 h-5 text-orange-500" />
        Constats Majeurs
      </h3>
      <div className="space-y-3">
        {findings.map((finding, idx) => (
          <div key={idx} className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg">
            <span className="bg-orange-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">
              {idx + 1}
            </span>
            <div className="text-slate-300">
              <MarkdownRenderer content={typeof finding === 'string' ? finding : finding.description || finding.title || JSON.stringify(finding)} variant="compact" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Recommendations component
const Recommendations = ({ recommendations }) => {
  if (!recommendations || recommendations.length === 0) return null;

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
      <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
        <CheckSquare className="w-5 h-5 text-emerald-500" />
        Recommandations
      </h3>
      <div className="space-y-3">
        {recommendations.map((rec, idx) => {
          const recContent = typeof rec === 'string' ? rec : rec.action || rec.description || rec.title || JSON.stringify(rec);
          return (
            <div key={idx} className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <MarkdownRenderer content={recContent} variant="compact" />
                {rec.priority && (
                  <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded ${
                    rec.priority === 'P1' ? 'bg-red-500/20 text-red-400' :
                    rec.priority === 'P2' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-blue-500/20 text-blue-400'
                  }`}>
                    {rec.priority}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// MITRE ATT&CK mapping component
const MitreMapping = ({ mapping }) => {
  if (!mapping || Object.keys(mapping).length === 0) return null;

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
      <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
        <Target className="w-5 h-5 text-red-500" />
        Mapping MITRE ATT&CK
      </h3>
      <div className="space-y-4">
        {Object.entries(mapping).map(([tactic, techniques]) => (
          <div key={tactic} className="bg-slate-900/50 rounded-lg p-4">
            <div className="font-medium text-slate-200 mb-2">{tactic}</div>
            <div className="flex flex-wrap gap-2">
              {(Array.isArray(techniques) ? techniques : [techniques]).map((tech, idx) => (
                <span key={idx} className="px-2 py-1 bg-red-500/20 text-red-300 text-xs rounded-lg">
                  {typeof tech === 'string' ? tech : tech.id || tech.name}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Vulnerability stats component
const VulnStats = ({ stats }) => {
  if (!stats) return null;

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
      <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
        <PieChart className="w-5 h-5 text-blue-500" />
        Statistiques Vulnérabilités
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-red-500/20 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-red-400">{stats.critical || 0}</div>
          <div className="text-sm text-red-300">Critiques</div>
        </div>
        <div className="bg-orange-500/20 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-orange-400">{stats.high || 0}</div>
          <div className="text-sm text-orange-300">Élevées</div>
        </div>
        <div className="bg-yellow-500/20 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-yellow-400">{stats.medium || 0}</div>
          <div className="text-sm text-yellow-300">Moyennes</div>
        </div>
        <div className="bg-green-500/20 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-green-400">{stats.low || 0}</div>
          <div className="text-sm text-green-300">Faibles</div>
        </div>
      </div>
    </div>
  );
};

// Remediation progress component (for retest reports)
const RemediationProgress = ({ progress }) => {
  if (!progress) return null;

  const total = (progress.fixed || 0) + (progress.partial || 0) + (progress.not_fixed || 0);
  const fixedPercent = total > 0 ? Math.round((progress.fixed || 0) / total * 100) : 0;

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
      <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
        <RefreshCw className="w-5 h-5 text-purple-500" />
        Progression Remediation
      </h3>

      {/* Progress bar */}
      <div className="mb-6">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-slate-400">Taux de correction</span>
          <span className="text-white font-bold">{fixedPercent}%</span>
        </div>
        <div className="h-3 bg-slate-700 rounded-full overflow-hidden flex">
          <div className="bg-green-500 h-full" style={{ width: `${progress.fixed ? progress.fixed / total * 100 : 0}%` }} />
          <div className="bg-yellow-500 h-full" style={{ width: `${progress.partial ? progress.partial / total * 100 : 0}%` }} />
          <div className="bg-red-500 h-full" style={{ width: `${progress.not_fixed ? progress.not_fixed / total * 100 : 0}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg">
          <CheckSquare className="w-5 h-5 text-green-500" />
          <div>
            <div className="text-xl font-bold text-green-400">{progress.fixed || 0}</div>
            <div className="text-xs text-green-300">Corrigées</div>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 bg-yellow-500/10 rounded-lg">
          <MinusSquare className="w-5 h-5 text-yellow-500" />
          <div>
            <div className="text-xl font-bold text-yellow-400">{progress.partial || 0}</div>
            <div className="text-xs text-yellow-300">Partielles</div>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 bg-red-500/10 rounded-lg">
          <XSquare className="w-5 h-5 text-red-500" />
          <div>
            <div className="text-xl font-bold text-red-400">{progress.not_fixed || 0}</div>
            <div className="text-xs text-red-300">Non corrigées</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Export Modal Component
const ExportModal = ({ isOpen, onClose, format, reportName, reportId, onExportWithEmail, t }) => {
  const [showLongWaitMessage, setShowLongWaitMessage] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setShowLongWaitMessage(false);
      setEmailSent(false);
      // After 8 seconds, show the long wait message
      timerRef.current = setTimeout(() => {
        setShowLongWaitMessage(true);
      }, 8000);
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isOpen]);

  const handleCloseAndSendEmail = async () => {
    setSendingEmail(true);
    try {
      // Call export with send_email: true
      await generatedReports.export(reportId, format, true);
      setEmailSent(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Error sending email:', err);
      onClose();
    } finally {
      setSendingEmail(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
        {emailSent ? (
          // Success message
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              {t('reports.export.emailSent', 'E-mail envoyé !')}
            </h3>
            <p className="text-slate-400">
              {t('reports.export.emailSentDesc', 'Vous recevrez le lien de téléchargement par e-mail dans quelques instants.')}
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <FileDown className="w-5 h-5 text-emerald-500" />
                {t('reports.export.title', 'Exportation')}
              </h3>
              <button
                onClick={onClose}
                className="p-1 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            {/* Loading animation */}
            <div className="text-center py-6">
              <div className="relative w-20 h-20 mx-auto mb-4">
                <div className="absolute inset-0 border-4 border-slate-700 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-emerald-500 rounded-full border-t-transparent animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <FileDown className="w-8 h-8 text-emerald-500" />
                </div>
              </div>

              {!showLongWaitMessage ? (
                // Initial message
                <div>
                  <p className="text-white font-medium mb-1">
                    {t('reports.export.processing', 'Exportation en cours...')}
                  </p>
                  <p className="text-slate-400 text-sm">
                    {t('reports.export.generating', `Génération du fichier ${format?.toUpperCase()}`)}
                  </p>
                </div>
              ) : (
                // Long wait message
                <div>
                  <p className="text-yellow-400 font-medium mb-2">
                    {t('reports.export.takingLong', 'Cette opération prend plus de temps que prévu.')}
                  </p>
                  <p className="text-slate-400 text-sm mb-6">
                    {t('reports.export.canWaitOrEmail', 'Vous pouvez patienter, ou fermer cette fenêtre et nous vous enverrons un lien par e-mail.')}
                  </p>

                  {/* Email option button */}
                  <button
                    onClick={handleCloseAndSendEmail}
                    disabled={sendingEmail}
                    className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-500 rounded-xl font-medium text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {sendingEmail ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Mail className="w-5 h-5" />
                    )}
                    {t('reports.export.sendByEmail', 'Recevoir par e-mail')}
                  </button>
                </div>
              )}
            </div>

            {/* Continue waiting hint */}
            {showLongWaitMessage && (
              <p className="text-center text-slate-500 text-xs mt-4">
                {t('reports.export.continueWaiting', 'Vous pouvez aussi continuer à attendre, le téléchargement démarrera automatiquement.')}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Main component
const GeneratedReportDetail = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const user = useAuthStore(state => state.user);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [exportModal, setExportModal] = useState({ isOpen: false, format: null });

  // Check if user can export Word (entreprise, groupe, dedicated plans or superadmin)
  const canExportWord = () => {
    if (!user) return false;
    if (user.is_superadmin) return true;
    const allowedPlans = ['entreprise', 'groupe', 'dedicated'];
    return user.subscription_plan && allowedPlans.includes(user.subscription_plan.toLowerCase());
  };

  // Filter export formats based on user permissions
  const getAvailableFormats = () => {
    if (canExportWord()) {
      return EXPORT_FORMATS;
    }
    return EXPORT_FORMATS.filter(f => f.id !== 'docx');
  };

  // Fetch report
  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const data = await generatedReports.get(reportId);
      setReport(data);
    } catch (err) {
      console.error('Error fetching report:', err);
      setError('Erreur lors du chargement du rapport');
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Auto-refresh for generating status
  useEffect(() => {
    if (report?.status === 'generating') {
      const interval = setInterval(async () => {
        try {
          const status = await generatedReports.getStatus(reportId);
          setReport(prev => ({ ...prev, ...status }));
          if (status.status !== 'generating') {
            fetchReport();
          }
        } catch (err) {
          console.error('Error polling status:', err);
        }
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [report?.status, reportId, fetchReport]);

  // Export handler with modal
  const handleExport = async (format) => {
    setShowExportMenu(false);
    setExportModal({ isOpen: true, format });
    setExporting(true);

    try {
      await generatedReports.export(reportId, format);
      const blob = await generatedReports.download(reportId, format);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${report.name || 'report'}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Close modal on success
      setExportModal({ isOpen: false, format: null });
    } catch (err) {
      console.error('Error exporting report:', err);
      // Keep modal open on error to allow email fallback
    } finally {
      setExporting(false);
    }
  };

  // Handle export modal close (can trigger email send)
  const handleExportModalClose = () => {
    setExportModal({ isOpen: false, format: null });
    setExporting(false);
  };

  // Export with email notification
  const handleExportWithEmail = async (format) => {
    try {
      await generatedReports.export(reportId, format, true);
    } catch (err) {
      console.error('Error requesting email export:', err);
    }
  };

  // Regenerate handler
  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      await generatedReports.regenerate(reportId);
      await fetchReport();
    } catch (err) {
      console.error('Error regenerating report:', err);
    } finally {
      setRegenerating(false);
    }
  };

  // Delete handler
  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce rapport ?')) return;

    try {
      await generatedReports.delete(reportId);
      navigate('/reports/generated');
    } catch (err) {
      console.error('Error deleting report:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-emerald-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Chargement du rapport...</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Erreur</h2>
          <p className="text-slate-400 mb-4">{error || 'Rapport introuvable'}</p>
          <button
            onClick={() => navigate('/reports/generated')}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg font-medium text-white transition-colors"
          >
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  const typeConfig = REPORT_TYPES[report.report_type] || REPORT_TYPES.executive;
  const Icon = typeConfig.icon;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/reports/generated')}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour aux rapports
        </button>

        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className={`${typeConfig.color} p-4 rounded-xl`}>
              <Icon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{report.name}</h1>
              <p className="text-slate-400 mt-1">{typeConfig.name}</p>
              <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  {new Date(report.created_at).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
                {report.model_used && (
                  <span className="flex items-center gap-1.5">
                    <Brain className="w-4 h-4" />
                    {report.model_used}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Status */}
            {report.status === 'generating' && (
              <span className="px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg text-sm flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Génération en cours...
              </span>
            )}

            {/* Export button */}
            {report.status === 'completed' && (
              <div className="relative">
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  disabled={exporting}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg font-medium text-white transition-colors flex items-center gap-2"
                >
                  {exporting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Exporter
                  <ChevronDown className="w-4 h-4" />
                </button>

                {showExportMenu && (
                  <div className="absolute top-full right-0 mt-2 w-56 bg-slate-800 border border-slate-700 rounded-xl shadow-xl z-50 py-2">
                    {EXPORT_FORMATS.map(format => {
                      const isWordLocked = format.id === 'docx' && !canExportWord();
                      return (
                        <button
                          key={format.id}
                          onClick={() => !isWordLocked && handleExport(format.id)}
                          disabled={isWordLocked}
                          className={`w-full px-4 py-2 text-left text-sm flex items-center gap-3 ${
                            isWordLocked
                              ? 'text-slate-500 cursor-not-allowed'
                              : 'text-slate-300 hover:bg-slate-700'
                          }`}
                          title={isWordLocked ? t('reports.export.wordLocked', 'Disponible pour les plans Entreprise et supérieurs') : ''}
                        >
                          <format.icon className={`w-4 h-4 ${isWordLocked ? 'text-slate-500' : format.color}`} />
                          <span className="flex-1">{format.name}</span>
                          {isWordLocked && (
                            <Lock className="w-3.5 h-3.5 text-slate-500" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Regenerate */}
            <button
              onClick={handleRegenerate}
              disabled={regenerating || report.status === 'generating'}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg font-medium text-white transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {regenerating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Régénérer
            </button>

            {/* Delete */}
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600/20 hover:bg-red-600/30 rounded-lg font-medium text-red-400 transition-colors flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Quality & Risk indicators */}
      {report.status === 'completed' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* Quality score */}
          {report.quality_score !== null && (
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-emerald-500/20 rounded-lg">
                  <Award className="w-6 h-6 text-emerald-500" />
                </div>
                <div>
                  <div className="text-sm text-slate-400">Score Qualité</div>
                  <div className="text-2xl font-bold text-white">{Math.round(report.quality_score)}%</div>
                </div>
              </div>
            </div>
          )}

          {/* Risk level */}
          {report.risk_level && (
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-lg ${
                  report.risk_level === 'critical' ? 'bg-red-500/20' :
                  report.risk_level === 'high' ? 'bg-orange-500/20' :
                  report.risk_level === 'medium' ? 'bg-yellow-500/20' : 'bg-green-500/20'
                }`}>
                  <AlertTriangle className={`w-6 h-6 ${
                    report.risk_level === 'critical' ? 'text-red-500' :
                    report.risk_level === 'high' ? 'text-orange-500' :
                    report.risk_level === 'medium' ? 'text-yellow-500' : 'text-green-500'
                  }`} />
                </div>
                <div>
                  <div className="text-sm text-slate-400">Niveau de Risque</div>
                  <div className={`text-2xl font-bold ${
                    report.risk_level === 'critical' ? 'text-red-400' :
                    report.risk_level === 'high' ? 'text-orange-400' :
                    report.risk_level === 'medium' ? 'text-yellow-400' : 'text-green-400'
                  }`}>
                    {
                      report.risk_level === 'critical' ? 'Critique' :
                      report.risk_level === 'high' ? 'Élevé' :
                      report.risk_level === 'medium' ? 'Moyen' : 'Faible'
                    }
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Risk score */}
          {report.risk_score !== null && (
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-500/20 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <div className="text-sm text-slate-400">Score de Risque</div>
                  <div className="text-2xl font-bold text-white">{report.risk_score}/100</div>
                </div>
              </div>
            </div>
          )}

          {/* Verification score */}
          {report.generated_sections?._verification?.confidence_score != null && (
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-cyan-500/20 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-cyan-500" />
                </div>
                <div>
                  <div className="text-sm text-slate-400">Vérification IA</div>
                  <div className="text-2xl font-bold text-white">
                    {Math.round(report.generated_sections._verification.confidence_score)}% vérifié
                  </div>
                  {report.generated_sections._verification.corrected > 0 && (
                    <div className="text-xs text-yellow-400 mt-0.5">
                      {report.generated_sections._verification.corrected} correction{report.generated_sections._verification.corrected > 1 ? 's' : ''} appliquée{report.generated_sections._verification.corrected > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Executive Summary */}
      {report.executive_summary && (
        <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 rounded-xl p-6 mb-6">
          <h2 className="font-bold text-white text-lg mb-3 flex items-center gap-2">
            <Brain className="w-5 h-5 text-blue-400" />
            Résumé Exécutif
          </h2>
          <MarkdownRenderer content={report.executive_summary} variant="default" />
        </div>
      )}

      {/* Main content */}
      <div className="space-y-6">
        {/* Vulnerability Stats */}
        <VulnStats stats={report.vuln_stats} />

        {/* Remediation Progress (for retest) */}
        <RemediationProgress progress={report.remediation_progress} />

        {/* Key Findings */}
        <KeyFindings findings={report.key_findings} />

        {/* Recommendations */}
        <Recommendations recommendations={report.recommendations} />

        {/* MITRE Mapping */}
        <MitreMapping mapping={report.mitre_mapping} />

        {/* Generated Sections */}
        {report.generated_sections && Object.entries(report.generated_sections).map(([key, section]) => (
          <ReportSection
            key={key}
            title={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            content={section?.content || section}
            icon={FileText}
          />
        ))}

        {/* Attack Vectors */}
        {report.attack_vectors && report.attack_vectors.length > 0 && (
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
            <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-red-500" />
              Vecteurs d'Attaque
            </h3>
            <div className="space-y-3">
              {report.attack_vectors.map((vector, idx) => (
                <div key={idx} className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                  <div className="font-medium text-red-400 mb-2">
                    {typeof vector === 'string' ? vector : vector.name || vector.title}
                  </div>
                  {vector.description && (
                    <p className="text-slate-400 text-sm">{vector.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Compliance Impact */}
        {report.compliance_impact && Object.keys(report.compliance_impact).length > 0 && (
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
            <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-500" />
              Impact Conformité
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {Object.entries(report.compliance_impact).map(([standard, impact]) => (
                <div key={standard} className="bg-slate-900/50 rounded-lg p-4 text-center">
                  <div className="font-medium text-slate-200 mb-1">{standard}</div>
                  <div className={`text-sm ${
                    impact === 'high' ? 'text-red-400' :
                    impact === 'medium' ? 'text-yellow-400' : 'text-green-400'
                  }`}>
                    {
                      impact === 'critical' ? 'Critique' :
                      impact === 'high' ? 'Élevé' :
                      impact === 'medium' ? 'Moyen' : 'Faible'
                    }
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Generation info footer */}
      <div className="mt-8 pt-6 border-t border-slate-700 text-sm text-slate-500">
        <div className="flex flex-wrap items-center gap-6">
          {report.tokens_used && (
            <span>Tokens utilisés : {report.tokens_used.toLocaleString()}</span>
          )}
          {report.generation_time_seconds && (
            <span>Temps de génération : {Math.round(report.generation_time_seconds)}s</span>
          )}
          {report.prompt_version && (
            <span>Version prompt: {report.prompt_version}</span>
          )}
        </div>
      </div>

      {/* Export Modal */}
      <ExportModal
        isOpen={exportModal.isOpen}
        onClose={handleExportModalClose}
        format={exportModal.format}
        reportName={report?.name}
        reportId={reportId}
        onExportWithEmail={handleExportWithEmail}
        t={t}
      />
    </div>
  );
};

export default GeneratedReportDetail;
